import type { ComponentConfig, PropertyPage, CheckboxOptions } from './types';
import { getCheckboxOptions } from './types';
import { isTransformNameUnique } from '../../utils/validationUtils';
import { useStore } from '../../store/store';

export interface ValueChangeEvent {
  componentId: string;
  value: any;
  pageId: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string[]>;
}

export interface PropertyValueManagerConfig {
  initialValues?: Record<string, any>;
  pages: PropertyPage[];
  onChange?: (values: Record<string, any>) => void;
  onValidationChange?: (validation: Record<string, boolean>) => void;
}

export class PropertyValueManager {
  private values: Record<string, any> = {};
  private errors: Record<string, string[]> = {};
  private pageValidation: Record<string, boolean> = {};
  private config: PropertyValueManagerConfig;
  private changeListeners: Array<(values: Record<string, any>) => void> = [];
  private validationListeners: Array<(validation: Record<string, boolean>) => void> = [];

  constructor(config: PropertyValueManagerConfig) {
    this.config = config;
    this.values = { ...config.initialValues || {} };
    
    // Initialize page validation
    this.config.pages.forEach(page => {
      this.pageValidation[page.id] = this.validatePage(page).isValid;
    });
  }

  // Value management
  getValue(componentId: string): any {
    return this.values[componentId];
  }

  getValues(): Record<string, any> {
    return { ...this.values };
  }

  setValue(componentId: string, value: any, pageId?: string): void {
    const oldValue = this.values[componentId];
    this.values[componentId] = value;

    // Clear any existing errors for this component
    if (this.errors[componentId]) {
      const newErrors = { ...this.errors };
      delete newErrors[componentId];
      this.errors = newErrors;
    }

    // Re-validate relevant pages
    if (pageId) {
      const page = this.config.pages.find(p => p.id === pageId);
      if (page) {
        this.updatePageValidation(page);
      }
    } else {
      // Re-validate all pages if no page specified
      this.config.pages.forEach(page => {
        this.updatePageValidation(page);
      });
    }

    // Notify listeners if value actually changed
    if (oldValue !== value) {
      this.notifyChange();
      this.notifyValidationChange();
    }
  }

  setValues(newValues: Record<string, any>): void {
    const hasChanges = Object.keys(newValues).some(key => 
      this.values[key] !== newValues[key]
    );

    this.values = { ...this.values, ...newValues };
    
    // Re-validate all pages
    this.config.pages.forEach(page => {
      this.updatePageValidation(page);
    });

    if (hasChanges) {
      this.notifyChange();
      this.notifyValidationChange();
    }
  }

  handleValueChange(event: ValueChangeEvent): void {
    this.setValue(event.componentId, event.value, event.pageId);
    
    // Handle clearing components when checkbox is checked
    if (event.value === true) { // Only when checkbox is checked
      const allComponents = this.config.pages.flatMap(page => page.components);
      const checkboxComponent = allComponents.find(comp => comp.id === event.componentId);
      
      if (checkboxComponent && checkboxComponent.type === 'checkbox') {
        const checkboxOptions = getCheckboxOptions(checkboxComponent);
        
        if (checkboxOptions.clearComponents && Array.isArray(checkboxOptions.clearComponents)) {
          // Clear the specified components to their default values
          checkboxOptions.clearComponents.forEach((componentId: string) => {
            const targetComponent = allComponents.find(comp => comp.id === componentId);
            if (targetComponent) {
              const defaultValue = targetComponent.defaultValue;
              this.setValue(componentId, defaultValue, event.pageId);
            }
          });
        }
      }
    }
  }

  // Validation management
  validateComponent(component: ComponentConfig, value: any, allValues: Record<string, any>): string[] {
    const errors: string[] = [];

    // Required validation
    if (component.required && (value === undefined || value === '' || value === null)) {
      errors.push(`${component.label} is required`);
    }

    // Type-specific validation
    if (component.type === 'text' && value) {
      const textConfig = component as any;
      if (textConfig.minLength && value.length < textConfig.minLength) {
        errors.push(`${component.label} must be at least ${textConfig.minLength} characters`);
      }
      if (textConfig.maxLength && value.length > textConfig.maxLength) {
        errors.push(`${component.label} must not exceed ${textConfig.maxLength} characters`);
      }
    }

    // Cross-field validation (for dropdowns)
    if (component.type === 'dropdown' && component.additionalOptions) {
      const dropdownOptions = component.additionalOptions as any;
      if (dropdownOptions.validateAgainst && value && value.toString().trim() !== '') {
        const validateAgainstValue = allValues[dropdownOptions.validateAgainst];
        if (validateAgainstValue && value === validateAgainstValue) {
          errors.push(dropdownOptions.validationMessage || `${component.label} must be different from ${dropdownOptions.validateAgainst}`);
        }
      }
    }

    // Custom validation rules
    component.validation?.forEach(rule => {
      switch (rule.type) {
        case 'pattern':
          if (value && !new RegExp(rule.value).test(value)) {
            errors.push(rule.message);
          }
          break;
        case 'uniqueInPipeline':
          if (value && value.trim() !== '') {
            // Get current selected node ID for exclusion from uniqueness check
            const state = useStore.getState();
            const activePipeline = state.pipelines.find(p => p.id === state.activePipelineId);
            const selectedNodeId = activePipeline?.selectedNodeId;
            
            const isUnique = isTransformNameUnique(value, selectedNodeId || undefined);
            if (!isUnique) {
              errors.push(rule.message || 'This name is already used by another transform');
            }
          }
          break;
        case 'custom':
          if (typeof rule.value === 'function') {
            try {
              const result = rule.value(value, allValues);
              if (!result) {
                errors.push(rule.message);
              }
            } catch (error) {
              errors.push(rule.message);
            }
          }
          break;
      }
    });

    return errors;
  }

  validatePage(page: PropertyPage): ValidationResult {
    const pageErrors: Record<string, string[]> = {};
    let isValid = true;

    page.components.forEach(component => {
      const componentValue = this.values[component.id];
      const componentErrors = this.validateComponent(component, componentValue, this.values);

      if (componentErrors.length > 0) {
        pageErrors[component.id] = componentErrors;
        isValid = false;
      }
    });

    return { isValid, errors: pageErrors };
  }

  validateAllPages(): ValidationResult {
    const allErrors: Record<string, string[]> = {};
    let isValid = true;

    this.config.pages.forEach(page => {
      const pageValidation = this.validatePage(page);
      if (!pageValidation.isValid) {
        isValid = false;
        Object.assign(allErrors, pageValidation.errors);
      }
    });

    return { isValid, errors: allErrors };
  }

  private updatePageValidation(page: PropertyPage): void {
    const validation = this.validatePage(page);
    this.pageValidation[page.id] = validation.isValid;
    
    // Update component errors
    Object.keys(validation.errors).forEach(componentId => {
      this.errors[componentId] = validation.errors[componentId];
    });

    // Clear errors for components that are now valid
    page.components.forEach(component => {
      if (!validation.errors[component.id] && this.errors[component.id]) {
        delete this.errors[component.id];
      }
    });
  }

  // Error management
  getErrors(): Record<string, string[]> {
    return { ...this.errors };
  }

  getComponentErrors(componentId: string): string[] | undefined {
    return this.errors[componentId];
  }

  hasErrors(): boolean {
    return Object.keys(this.errors).length > 0;
  }

  // Page validation
  getPageValidation(): Record<string, boolean> {
    return { ...this.pageValidation };
  }

  isPageValid(pageId: string): boolean {
    return this.pageValidation[pageId] ?? false;
  }

  hasRequiredFieldsError(page: PropertyPage): boolean {
    return page.components.some(component => {
      if (!component.required) return false;
      
      const componentValue = this.values[component.id];
      return componentValue === undefined || 
             componentValue === '' || 
             componentValue === null ||
             (Array.isArray(componentValue) && componentValue.length === 0);
    });
  }

  // Listener management
  addChangeListener(listener: (values: Record<string, any>) => void): () => void {
    this.changeListeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.changeListeners.indexOf(listener);
      if (index > -1) {
        this.changeListeners.splice(index, 1);
      }
    };
  }

  addValidationListener(listener: (validation: Record<string, boolean>) => void): () => void {
    this.validationListeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.validationListeners.indexOf(listener);
      if (index > -1) {
        this.validationListeners.splice(index, 1);
      }
    };
  }

  private notifyChange(): void {
    const values = this.getValues();
    this.changeListeners.forEach(listener => listener(values));
    
    // Also call the config onChange if provided
    if (this.config.onChange) {
      this.config.onChange(values);
    }
  }

  private notifyValidationChange(): void {
    const validation = this.getPageValidation();
    this.validationListeners.forEach(listener => listener(validation));
    
    // Also call the config onValidationChange if provided
    if (this.config.onValidationChange) {
      this.config.onValidationChange(validation);
    }
  }

  // Utility methods
  reset(newValues?: Record<string, any>): void {
    this.values = { ...newValues || this.config.initialValues || {} };
    this.errors = {};
    
    // Re-validate all pages
    this.config.pages.forEach(page => {
      this.updatePageValidation(page);
    });

    this.notifyChange();
    this.notifyValidationChange();
  }

  clone(): PropertyValueManager {
    const newConfig = {
      ...this.config,
      initialValues: this.getValues()
    };
    return new PropertyValueManager(newConfig);
  }
} 